import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { QueryModel } from 'src/app/shared/common/query.model';
import { ApplicationConfigService } from 'src/app/core/config/application-config.service';
import { CommonService } from 'src/app/shared/services/common.service';

import { EkycListType, ServiceRequestStatus, ServiceRequestType } from './ekyc-request.enum';
import { IServiceRequest } from './ekyc-request.model';

export class IServiceEnrollmentFilter extends QueryModel {
  serviceRequestId?: string | null;
  serviceType?: string | null;
  nid?: string | null;
  mobileNumber?: string | null;
  accountNumber?: string | null;
  branchCode?: string | null;
  status?: string | null;
  fromDate?: string | null;
  toDate?: string | null;
}

/**
 * A service request joined to the parent application, which is what the queue actually shows.
 * The requests themselves live inside their parent record — SRS §M10 tracks each independently
 * but they never exist apart from the application that produced them.
 */
export interface IServiceEnrollmentRow extends IServiceRequest {
  applicantName: string;
  /** Nullable because the parent's smartNid is — an application may carry only an old NID. */
  nid: string | null;
  phoneNumber: string;
  accountNumber: string | null;
  ekycType: string;
  branchName: string;
  dateRequested: string;
}

/** Service enrollment queues — contracts-1 §8. */
@Injectable({ providedIn: 'root' })
export class ServiceEnrollmentService {
  private readonly resourceUrl: string;

  constructor(
    protected readonly http: HttpClient,
    protected readonly commonService: CommonService,
    applicationConfigService: ApplicationConfigService,
  ) {
    this.resourceUrl = applicationConfigService.getEndpointFor('account/admin/assisted-ekyc/service-requests');
  }

  fetchList(listType: EkycListType, query: IServiceEnrollmentFilter): Observable<any> {
    const segment = listType === 'all' ? '' : `/${listType}`;
    return this.http.get<any>(`${this.resourceUrl}${segment}?${this.commonService.toQueryString(query)}`);
  }

  details(serviceRequestId: string): Observable<any> {
    return this.http.get<any>(`${this.resourceUrl}/${serviceRequestId}`);
  }

  /** Manual retry after automatic retries are exhausted — SRS §M10. */
  retry(serviceRequestId: string): Observable<any> {
    return this.http.post<any>(`${this.resourceUrl}/${serviceRequestId}/retry`, {});
  }

  /** Closing an exception by hand always carries a reason. */
  manuallyClose(serviceRequestId: string, reason: string): Observable<any> {
    return this.http.post<any>(`${this.resourceUrl}/${serviceRequestId}/manual-close`, { reason });
  }

  updateStatus(serviceRequestId: string, status: ServiceRequestStatus, remarks: string): Observable<any> {
    return this.http.post<any>(`${this.resourceUrl}/${serviceRequestId}/status`, { status, remarks });
  }

  /** Daily reconciliation: requested vs acknowledged vs failed vs manually closed. */
  reconciliation(serviceType?: ServiceRequestType): Observable<any> {
    return this.http.get<any>(`${this.resourceUrl}/reconciliation?serviceType=${serviceType ?? ''}`);
  }
}
